import { useEffect, useId, useRef } from "react";
import { track } from "@/lib/analytics";

export type TokenoTone = "saffron" | "mint" | "coral" | "violet";

export type TokenoMetric = {
  key: string;
  label: string;
  value: string;
  desc: string;
  tone: TokenoTone;
  icon: React.ComponentType<{ className?: string }>;
  details: {
    explanation: string;
    stats: { label: string; value: string }[];
  };
};

export const TONE_COLOR: Record<TokenoTone, string> = {
  saffron: "oklch(0.82 0.18 75)",
  mint: "oklch(0.86 0.16 165)",
  coral: "oklch(0.74 0.19 30)",
  violet: "oklch(0.66 0.18 280)",
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export type TokenoModalLabels = {
  toneSuffix?: string; // shown after tone (e.g. " · metric" or "-Metrik")
  detailView?: string; // footer left eyebrow (e.g. "· detail view" / "· Detailansicht")
  verified?: string; // footer right badge (e.g. "verified" / "verifiziert")
  closeAria?: string; // template containing "{label}" placeholder
  valueAria?: string; // template containing "{value}" placeholder
};

export function TokenoModal({
  metric,
  onClose,
  labels,
}: {
  metric: TokenoMetric;
  onClose: () => void;
  labels?: TokenoModalLabels;
}) {
  const color = TONE_COLOR[metric.tone];
  const Icon = metric.icon;
  const titleId = useId();
  const descId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    track("modal_open", { id: "tokeno", metric: metric.key });
    return () => track("modal_close", { id: "tokeno", metric: metric.key });
  }, [metric.key]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const getFocusable = (): HTMLElement[] => {
      const root = dialogRef.current;
      if (!root) return [];
      return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute("inert"),
      );
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const focusable = getFocusable();
        if (focusable.length === 0) {
          e.preventDefault();
          dialogRef.current?.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement as HTMLElement | null;
        const root = dialogRef.current;
        if (e.shiftKey && (active === first || !root?.contains(active))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (active === last || !root?.contains(active))) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusRaf =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame(() => closeRef.current?.focus())
        : (setTimeout(() => closeRef.current?.focus(), 0) as unknown as number);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      if (typeof cancelAnimationFrame === "function") cancelAnimationFrame(focusRaf);
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      data-testid="tokeno-modal-root"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.currentTarget === e.target) onClose();
      }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-obsidian/80 backdrop-blur-xl"
        style={{ animation: "fade-in 0.3s ease-out" }}
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        tabIndex={-1}
        className="relative w-full max-w-lg rounded-3xl glass-strong luminous-border p-6 sm:p-8 focus:outline-none"
        style={{
          animation: "scale-in 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)",
          boxShadow: `0 40px 100px -40px ${color}, 0 0 0 1px oklch(1 0 0 / 0.1) inset`,
        }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-3xl opacity-40 blur-2xl"
          style={{ background: `radial-gradient(60% 50% at 50% 40%, ${color}, transparent 70%)` }}
        />

        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label={(labels?.closeAria ?? "Close {label} details").replace(
            "{label}",
            metric.label,
          )}
          className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full glass transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          <svg
            aria-hidden="true"
            focusable="false"
            className="h-4 w-4 text-muted-foreground"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="relative flex items-center gap-4">
          <div
            className="grid h-12 w-12 place-items-center rounded-2xl glass-strong"
            style={{ boxShadow: `0 0 20px ${color}` }}
            aria-hidden="true"
          >
            <span style={{ color }}>
              <Icon className="h-6 w-6" />
            </span>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              {metric.tone}
              {labels?.toneSuffix ?? " · metric"}
            </div>
            <h2 id={titleId} className="mt-0.5 font-display text-2xl font-medium tracking-tight">
              {metric.label}
            </h2>
          </div>
        </div>

        <div
          className="relative mt-6 font-display text-[clamp(3rem,10vw,5rem)] font-medium leading-none tracking-tight"
          style={{ color }}
          aria-label={(labels?.valueAria ?? "Value: {value}").replace("{value}", metric.value)}
        >
          {metric.value}
        </div>

        <p
          id={descId}
          className="relative mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base"
        >
          {metric.details.explanation}
        </p>

        <div className="relative mt-6 grid grid-cols-2 gap-3">
          {metric.details.stats.map((s) => (
            <div
              key={s.label}
              className="rounded-2xl glass p-4 transition-colors duration-300 hover:bg-white/[0.04]"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {s.label}
              </div>
              <div
                className="mt-1 font-display text-xl font-semibold tracking-tight sm:text-2xl"
                style={{ color }}
              >
                {s.value}
              </div>
            </div>
          ))}
        </div>

        <div className="relative mt-6 flex items-center justify-between border-t border-white/5 pt-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            · {labels?.detailView ?? "detail view"}
          </span>
          <span
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em]"
            style={{ color }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ background: color, boxShadow: `0 0 8px ${color}` }}
            />
            {labels?.verified ?? "verified"}
          </span>
        </div>
      </div>
    </div>
  );
}
